<template>
    <modal modal_name="edit_topic" :title="translations['forum.edit_topic']" icon="pencil" type="large">
        <template v-slot:modal-content>
            <validation-observer ref="modal-edit_topic">
                <template v-if="topic">
                    <template v-if="topic.post_type !== 'poll'">
                        <template v-if="topic.user_id === user.id">
                            <form-row>
                                <form-field>
                                    <label class="form-label">{{ translations['forum.topic_title'] }}</label>

                                    <huddle-editor-v2   v-model="topic.title"
                                                        :single_line="true"
                                                        :allow_enter_shift="false"
                                                        :placeholder="translations['forum.topic_title']"
                                                        rules="required"
                                                        name="title">
                                    </huddle-editor-v2>
                                </form-field>
                            </form-row>

                            <form-row>
                                <form-field>
                                    <label class="form-label">{{ translations['forum.content_topic'] }}</label>

                                    <huddle-editor-v2   v-model="topic.content"
                                                        type="textarea"
                                                        :mentions="true"
                                                        :channelId="topic.board_id"
                                                        :emojis="true"
                                                        :placeholder="translations['forum.content_topic']"
                                                        rules="required|min:5"
                                                        name="content">
                                    </huddle-editor-v2>
                                </form-field>
                            </form-row>
                        </template>

                        <form-row v-if="topicIsPlanned && user.isAdmin">
                            <form-field>
                                <label class="form-label">{{ translations['forum.scheduled_date'] }} </label>

                                <div class="edit-topic-scheduled-date" :class="{'show': topic_scheduled_date}">
                                    <datetime ref="topicDatePicker"
                                              v-on:input="setTopicDate"
                                              :format="{ year: 'numeric', month: 'long', day: 'numeric', hour: 'numeric', minute: '2-digit', timeZoneName: 'short' }"
                                              v-on:close="toggleTopicScheduler"
                                              type="datetime"
                                              v-model="topic_scheduled_date"
                                              value-zone="Europe/Amsterdam"
                                              auto></datetime>
                                </div>
                            </form-field>
                        </form-row>

                        <div class="edit-video" v-if="topic.post_type === 'video'">
                            <form-row>
                                <form-field>
                                    <label class="form-label">Video URL</label>

                                    <huddle-editor-v2   v-model="topic.video_url"
                                                        type="textarea"
                                                        :single_line="true"
                                                        :allow_enter_shift="false"
                                                        placeholder="Youtube / Vimeo video URL"
                                                        :rules="{ required: true, regex: /^(http(s)??\:\/\/)?(www\.)?((youtube\.com\/watch\?v=)|(youtu.be\/))([a-zA-Z0-9\-_])+/ && /^.*(vimeo.com\/|v\/|u\/\w\/|\/|\|\))([^#\&\?]*).*/ }"
                                                        name="video">
                                    </huddle-editor-v2>

                                    <div class="video-preview-wrapper tw-mt-3 tw-rounded-lg tw-overflow-hidden">
                                        <div class="responsive-video embed-16-by-9" v-if="videoPreview">
                                            <iframe :src="videoPreview" class="topic-video" frameborder="0" allow="autoplay; encrypted-media" allowfullscreen></iframe>
                                        </div>
                                    </div>
                                </form-field>
                            </form-row>
                        </div>
                    </template>

                    <template v-if="topic.board_type === 'default' && topic.permissions.includes('EDIT_CHANNEL') && activeBoard">
                        <form-row>
                            <form-field>
                                <label class="form-label">{{ translations['social.relevant_board'] }}?</label>
                                <!-- boards -->
                                <div class="tw-flex tw-flex-wrap">
                                    <div class="new-board-select hover-opacity" v-for="board in filteredBoards" :style="{ background: board.color }" :class="{'is-active': board.id === activeBoard.id}" @click="updateEditPostBoard(board)">
                                        {{ board.name }}
                                    </div>
                                </div>

                                <!-- subboards -->
                                <div v-if="activeBoard && subboards.length > 0" class="subboards">
                                    <h3 class="secondary-c post-title">{{ translations['social.relevant_sub_board'] }}</h3>
                                    <div v-for="subboard in subboards" v-if="subboard.permissions.includes('WRITE')" class="new-board-select hover-opacity" :class="{'is-active': subboard.id === activeBoard.id}" @click="updateEditPostBoard(subboard)">
                                        {{ subboard.name }}
                                    </div>
                                </div>
                            </form-field>
                        </form-row>
                    </template>

                    <template v-if="user.isAdmin">
                        <form-row>
                            <form-field>
                                <label class="form-label">Slug</label>

                                <huddle-editor-v2   v-model="topic.slug"
                                                    :single_line="true"
                                                    :allow_enter_shift="false"
                                                    placeholder="Slug"
                                                    rules="required"
                                                    name="slug">
                                </huddle-editor-v2>
                            </form-field>
                        </form-row>

                        <form-row>
                            <form-field>
                                <label class="form-label">Meta Title</label>

                                <huddle-editor-v2   v-model="topic.meta_title"
                                                    :single_line="true"
                                                    :allow_enter_shift="false"
                                                    placeholder="Meta title"
                                                    rules="required"
                                                    name="meta title">
                                </huddle-editor-v2>
                            </form-field>
                        </form-row>

                        <form-row>
                            <form-field>
                                <label class="form-label">Meta description</label>

                                <huddle-editor-v2   v-model="topic.meta_description"
                                                    type="textarea"
                                                    placeholder="Meta description"
                                                    rules="required"
                                                    name="meta description">
                                </huddle-editor-v2>
                            </form-field>
                        </form-row>
                    </template>
                </template>
            </validation-observer>
        </template>

        <template v-slot:modal-bottom>
            <div class="subtle-button slim" @click="closePopup">
                {{ translations['fields.cancel'] }}
            </div>
            <div class="general-button btn-v2 slim" @click="submitEdit">
                {{ translations['forum.edit_topic'] }}
                <i class="far fa-check"></i>
            </div>
        </template>
    </modal>
</template>

<script>
    import { mapState } from 'vuex';
    import { Datetime } from 'vue-datetime';
    import filter from 'lodash.filter';
    import moment from 'moment';

    import Modal from '../../mixins/v2/Modal.js'
    import Global from '../../mixins/Global.js'
    import VideoRegexMixin from '../../mixins/v2/VideoRegexMixin.js'
    import Channel from "@/resources/assets/js/vue/lib/models/Channel";

    export default {
        mixins: [Modal, Global, VideoRegexMixin],
        name: 'EditTopic',

        components: {
            datetime: Datetime,
        },

        data() {
            return {
                topic_scheduler_opened: false,
                topic_scheduled_date: '',
            }
        },

        computed: {
            filteredBoards() {
                return filter(this.boards, function(board) {
                    return board.type !== 'moodboard' && board.homepage === true && board.permissions.includes('WRITE');
                });
            },

            //if posting a video, regex the link
            videoPreview() {
                if (this.topic.video_url !== '' && this.topic.post_type === 'video') {
                    return this.videoRegex(this.topic.video_url);
                }

                return false;
            },

            topicIsPlanned() {
                return moment(this.topic.release_date).isAfter(moment());
            },

            topic() {
                return this.popup_data;
            },

            boards() {
                return Channel.query().where('subgroup_id', null).where('template', 'default').where('source', 'system').get();
            },

            activeBoard() {
                return Channel.query().where('id', this.topic.board_id).first();
            },

            subboards() {
                if (this.activeBoard.subgroup_id !== null) {
                    return Channel.query().where('subgroup_id', this.activeBoard.subgroup_id).get();
                }

                return Channel.query().where('subgroup_id', this.activeBoard.id).get();
            },
        },

        methods: {
            updateEditPostBoard(board) {
                this.topic.board_id = board.id;
            },

            videoRegex(url) {
                return this.parsedVideo(url);
            },

            toggleTopicScheduler() {
                this.topic_scheduler_opened = !this.topic_scheduler_opened;

                if(!this.topic_scheduled_date) {
                    this.$nextTick(() => {
                        if(this.$refs.topicDatePicker) {
                            this.$refs.topicDatePicker.$el.firstElementChild.click();
                        }
                    });
                }
            },

            setTopicDate(date) {
                if(date) {
                    this.topic_scheduled_date = date;
                }
            },

            submitEdit() {
                const self = this;

                this.validateModal().then(() => {
                    this.$store.commit('toggleSpinner', true);

                    let channel = 0;
                    let release_date = this.topic.release_date;

                    if (this.activeBoard) {
                        channel = this.activeBoard;
                    }

                    if (this.topic.release_date) {
                        release_date = moment(this.topic.release_date).format('DD-MM-YYYY HH:mm');
                    }

                    const slug = this.topic.slug.toString().toLowerCase().replace(/\s+/g, '-').replace(/[^\w\-]+/g, '').replace(/\-\-+/g, '-');

                    this.$store.dispatch(
                        {
                            type: 'activeDataAction',
                            data: {
                                model: 'Topic',
                                crud_method: 'update',
                                sub_path: '/comments/' + this.topic.post_id,
                                save_data: {
                                    'conversation[id]': this.topic.id,
                                    id: this.topic.post_id,
                                    'content': this.topic.content,
                                    'title': this.topic.title,
                                    'video_url': this.videoPreview,
                                    channel_id: channel.id,
                                    'meta_title': this.topic.meta_title,
                                    'meta_description': this.topic.meta_description,
                                    'slug': slug,
                                    'conversation[release_date]': release_date,
                                },
                                success_callback: function (response) {
                                    if(response.success === false){
                                        failure_callback(response);
                                    }
                                    success_callback(channel, response)
                                },
                                failure_callback: function (response) {
                                    failure_callback(response);
                                }
                            }
                        }
                    );

                    let success_callback = function (channel) {
                        self.$store.commit('updateTopic', {
                            topic: self.topic,
                            title: self.topic.title,
                            content: self.topic.content,
                            videoRegexed: self.videoPreview,
                            channel_id: channel.id,
                            metaTitle: self.topic.meta_title,
                            metaDescription: self.topic.meta_description,
                            slug: slug,
                            reload_page: false,
                            release_date: self.topic.release_date,
                        });

                        self.$store.commit('toggleSpinner', false);
                    };

                    let failure_callback = function () {
                        self.$store.commit('toggleSpinner', false);
                    };
                }).catch(() => {});
            },
        },

        mounted() {
            if(this.topicIsPlanned) {
                this.topic_scheduled_date = moment(this.topic.release_date, moment.ISO_8601).format();
            }
        }
    };
</script>

<style lang="scss">
    .edit_topic-modal {
        .edit-video {
            margin-bottom: 15px;
        }

        .new-board-select {
            border-radius: 3px;
            color: #fff;
            padding: 5px;
            padding-left: 7px;
            padding-right: 7px;
            font-weight: 500;
            margin-right: 5px;
            margin-bottom: 9px;
            display: inline-block;
            background: dimgrey;
            font-size: 12px;

            &.is-active {
                opacity: 0.8;
            }
        }

        .subboards {
            @apply tw-flex tw-flex-wrap;
            margin-top: 5px;
        }

        .edit-topic-scheduled-date {
            position: relative;

            input {
                display: none;
                background: none;
                border: 0;
                padding: 0;
                margin-top: -5px;
                margin-bottom: 15px !important;
                color: #c7c7c7;
                font-size: 13px;

                &:hover {
                    cursor: pointer;
                }
            }

            &:hover {
                cursor: pointer;
            }

            &.show {
                &:before {
                    display: block;
                }

                input {
                    display: block;
                }
            }
        }
    }
</style>